import { NotificationsOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import EmployeeAvatar from 'components/@home/EmployeeAvatar';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { bool, func } from 'prop-types';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import channelShape from 'shapes/channel';
import { I18n } from 'utils/i18n';
import Label from './Label';
import LastMessage from './LastMessage';

const Channel = ({
  selected = false,
  onClick,
  channel,
  onClickJoin = null,
  multiple = false,
  isLast = false,
  fillRow = false,
}) => {
  const locale = useSelector(state => state.i18n.locale);
  return (
    <ListItem
      disableGutters
      sx={{
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
        p: 0,
        ...(fillRow && { height: '100%' }),
        ...(!channel.isMember && {
          '&:after': {
            top: 0,
            left: 0,
            content: `''`,
            position: 'absolute',
            backgroundColor: 'primary.ultraLight',
            opacity: 0.8,
            width: '100%',
            height: '100%',
          },
        }),
        ...(isLast && {
          '& .MuiListItemText-root': {
            borderBottom: 'none',
          },
        }),
      }}
    >
      <ListItemButton
        role={undefined}
        onClick={channel.isMember ? onClick : () => {}}
        disabled={!channel.isActive}
        selected={selected}
        disableGutters
        sx={{
          p: 0,
          ...(fillRow ? { height: '100%', alignItems: 'stretch' } : {}),
        }}
      >
        {multiple ? (
          <ListItemIcon
            sx={{
              ml: 3,
              minWidth: 0,
            }}
          >
            <Checkbox edge="start" checked={selected} tabIndex={-1} disableRipple />
          </ListItemIcon>
        ) : null}
        <Box sx={{ p: '15px 16px' }}>
          <EmployeeAvatar
            large
            employee={{
              avatar: channel.avatar,
              name: channel.name,
            }}
          />
        </Box>
        <ListItemText
          component="div"
          sx={{
            p: fillRow ? '0 16px 0 0' : '10px 16px 10px 0',
            m: 0,
            borderBottom: 1,
            borderColor: 'divider',
            ...(fillRow && {
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }),
          }}
          primary={
            <Label
              messagesUnreadCount={channel.messagesUnreadCount}
              mandatoriesUnreadCount={channel.mandatoriesUnreadCount}
              createdAt={channel.lastMessage?.createdAt}
              pinned={!!channel.pinned}
            >
              {channel.name} {channel.description ? ` - ${channel.description}` : ''}
            </Label>
          }
          secondary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LastMessage lastMessage={channel.lastMessage} channel={channel} />
              {channel.mutedUntil && (
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  }}
                >
                  <NotificationsOff
                    color="disabled"
                    sx={{ fontSize: '18px' }}
                    titleAccess={I18n.t('ChannelsPanel.MutedFor', {
                      duration: moment(channel.mutedUntil).locale(locale).fromNow(true),
                    })}
                  />
                </Box>
              )}
            </Box>
          }
        />
        {onClickJoin && !channel.isMember && (
          <Button
            onClick={onClickJoin}
            variant="contained"
            color="primary"
            sx={{
              position: 'absolute',
              left: '50%',
              zIndex: 2,
            }}
          >
            {I18n.t('ChannelsPanel.Join channel')}
          </Button>
        )}
      </ListItemButton>
    </ListItem>
  );
};
Channel.propTypes = {
  selected: bool,
  onClick: func.isRequired,
  channel: channelShape.isRequired,
  onClickJoin: func,
  multiple: bool,
  isLast: bool,
  fillRow: bool,
};

const areEqual = (prev, next) => {
  return (
    isEqual(prev.channel.lastMessage, next.channel.lastMessage) &&
    prev.channel.name === next.channel.name &&
    prev.channel._id === next.channel._id &&
    prev.channel.avatar === next.channel.avatar &&
    prev.channel.isActive === next.channel.isActive &&
    prev.channel.messagesUnreadCount === next.channel.messagesUnreadCount &&
    prev.channel.pinned === next.channel.pinned &&
    prev.selected === next.selected &&
    prev.onClick === next.onClick
  );
};
export default memo(Channel, areEqual);
